import React, { useState, useEffect } from "react";
import { Tables } from "@/lib/supabase";
import { ChevronDown, ChevronUp, Edit2, Trash2, Save, X } from "lucide-react";
import { Button } from "@/dentlab/src/components/ui/button";
import { Textarea } from "@/dentlab/src/components/ui/textarea";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/dentlab/src/components/ui/alert-dialog";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface JobNoteCardProps {
	note: Tables<"job_notes">;
	onUpdate: (noteId: number, content: string) => Promise<void>;
	onDelete: (noteId: number) => Promise<void>;
}

export const JobNoteCard: React.FC<JobNoteCardProps> = ({
	note,
	onUpdate,
	onDelete,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editContent, setEditContent] = useState("");
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		const content = (note.content as any)?.text || "";
		setEditContent(content);
	}, [note.content]);

	const getNoteContent = () => {
		const content = (note.content as any)?.text || "";
		return content;
	};

	const getAuthorName = () => {
		// For now, just display "Notiz" - can be enhanced later with user profiles
		return "Notiz";
	};

	const formatDate = (dateString: string) => {
		return format(new Date(dateString), "dd.MM.yyyy HH:mm", { locale: de });
	};

	const handleSave = async () => {
		setIsSaving(true);
		await onUpdate(note.id, editContent);
		setIsSaving(false);
		setIsEditing(false);
	};

	const handleCancel = () => {
		setEditContent(getNoteContent());
		setIsEditing(false);
	};

	const handleDelete = async () => {
		await onDelete(note.id);
		setShowDeleteDialog(false);
	};

	const noteContent = getNoteContent();
	const truncatedContent = noteContent.length > 100 && !isExpanded
		? noteContent.substring(0, 100) + "..."
		: noteContent;

	return (
		<>
			<div className="border rounded-lg p-3 space-y-2">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="flex-1">
						<div className="text-sm text-gray-600">
							{getAuthorName()} • {formatDate(note.created_at)}
							{note.modified_at !== note.created_at && (
								<span className="ml-2 text-xs text-gray-500">
									(bearbeitet {formatDate(note.modified_at)})
								</span>
							)}
						</div>
					</div>
					<div className="flex items-center gap-1">
						{!isEditing && (
							<>
								<Button
									variant="ghost"
									size="icon"
									className="h-7 w-7"
									onClick={() => setIsEditing(true)}
								>
									<Edit2 className="h-3.5 w-3.5" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									className="h-7 w-7"
									onClick={() => setShowDeleteDialog(true)}
								>
									<Trash2 className="h-3.5 w-3.5" />
								</Button>
							</>
						)}
						{!isEditing && noteContent.length > 100 && (
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								onClick={() => setIsExpanded(!isExpanded)}
							>
								{isExpanded ? (
									<ChevronUp className="h-3.5 w-3.5" />
								) : (
									<ChevronDown className="h-3.5 w-3.5" />
								)}
							</Button>
						)}
					</div>
				</div>

				{/* Content */}
				<div className="text-sm">
					{isEditing ? (
						<div className="space-y-2">
							<Textarea
								value={editContent}
								onChange={(e) => setEditContent(e.target.value)}
								className="min-h-[100px]"
								autoFocus
							/>
							<div className="flex justify-end gap-2">
								<Button
									size="sm"
									variant="outline"
									onClick={handleCancel}
									disabled={isSaving}
								>
									<X className="h-3.5 w-3.5 mr-1" />
									Abbrechen
								</Button>
								<Button
									size="sm"
									onClick={handleSave}
									disabled={!editContent.trim() || isSaving}
								>
									<Save className="h-3.5 w-3.5 mr-1" />
									{isSaving ? "Speichern..." : "Speichern"}
								</Button>
							</div>
						</div>
					) : (
						<div className="whitespace-pre-wrap">{truncatedContent}</div>
					)}
				</div>
			</div>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Notiz löschen</AlertDialogTitle>
						<AlertDialogDescription>
							Möchten Sie diese Notiz wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Abbrechen</AlertDialogCancel>
						<AlertDialogAction onClick={handleDelete}>
							Löschen
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};